import React, { useEffect, useMemo, useState } from 'react';

import { DIALOG_BUTTON, FILLED_SELECT, SOFT_BUTTON } from './organisationUserFormat';

import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

export const DEFAULT_PROJECT_ROLE = 'Level 1 - User';

const LABEL = 'tw-mb-0 tw-text-0.5sm tw-font-normal tw-normal-case tw-text-charcoal-75';

/**
 * "Add to project": one of the organisation's projects the member is not on,
 * and the project role to give them. `user` null keeps the dialog closed.
 */
export default function AddToProjectModal({ user, projects, roles, onClose, onAdd }) {
    const modal = useModal();
    const { show, hide } = modal;
    const [eventId, setEventId] = useState('');
    const [role, setRole] = useState(DEFAULT_PROJECT_ROLE);
    const [adding, setAdding] = useState(false);

    const available = useMemo(() => {
        const onProjects = new Set((user?.projects ?? []).map((project) => String(project.event_id)));

        return projects.filter((project) => !onProjects.has(String(project.value)));
    }, [user, projects]);

    useEffect(() => {
        if (user) {
            setEventId(available[0]?.value ?? '');
            setRole(roles.includes(DEFAULT_PROJECT_ROLE) ? DEFAULT_PROJECT_ROLE : (roles[0] ?? ''));
            setAdding(false);
            show();
        } else {
            hide();
        }
        // Reset only when the dialog opens for a member.
    }, [user, show, hide]);

    const add = () => {
        setAdding(true);
        Promise.resolve(onAdd(user, Number(eventId), role)).finally(() => setAdding(false));
    };

    return (
        <Modal modal={modal} modalIndexClass="tw-z-[110]" parentContainerClasses="tw-max-w-lg">
            {user && (
                <>
                    <ModalHeader title={$t('Add to project')} close={onClose} />
                    <div className="tw-space-y-4">
                        {available.length === 0 ? (
                            <p className="tw-mb-0 tw-text-0.5sm tw-text-charcoal-75">{$t('This user is already on every project in this organisation.')}</p>
                        ) : (
                            <div className="tw-grid tw-grid-cols-[150px_minmax(0,1fr)] tw-items-center tw-gap-x-3 tw-gap-y-3">
                                <label htmlFor="add-to-project-event" className={LABEL}>
                                    {$t('Project')}
                                </label>
                                <select
                                    id="add-to-project-event"
                                    className={`select-input ${FILLED_SELECT}`}
                                    value={eventId}
                                    onChange={(event) => setEventId(event.target.value)}
                                >
                                    {available.map((project) => (
                                        <option key={project.value} value={project.value}>
                                            {project.label}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="add-to-project-role" className={LABEL}>
                                    {$t('Project role')}
                                </label>
                                <select
                                    id="add-to-project-role"
                                    className={`select-input ${FILLED_SELECT}`}
                                    value={role}
                                    onChange={(event) => setRole(event.target.value)}
                                >
                                    {roles.map((name) => (
                                        <option key={name} value={name}>
                                            {$t(name)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="modal__footer">
                            <Button variant="secondary" className={SOFT_BUTTON} onClick={onClose} disabled={adding}>
                                {$t('Cancel')}
                            </Button>
                            <Button variant="primary" className={DIALOG_BUTTON} onClick={add} disabled={adding || !eventId || !role} loading={adding}>
                                {$t('Add')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
}
